import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { PageHeader } from "@/mockups/ui";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Check, Undo2, ArrowDownCircle, ArrowUpCircle } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const todayStr = () => new Date().toISOString().slice(0, 10);

const ESTADO = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  vencido: { label: "Vencido", cls: "bg-red-100 text-red-700 border-red-200" },
  cobrado: { label: "Cobrado", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  pagado: { label: "Pagado", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  borrador: { label: "Borrador", cls: "bg-muted text-muted-foreground border-border" },
  abonado: { label: "Abono", cls: "bg-muted text-muted-foreground border-border" },
};

const FILTERS = [
  { value: "pendientes", label: "Pendientes + vencidos" },
  { value: "vencido", label: "Solo vencidos" },
  { value: "pendiente", label: "Solo pendientes" },
];

function EstadoBadge({ estado, dias }) {
  const e = ESTADO[estado] || ESTADO.pendiente;
  return (
    <Badge variant="outline" className={e.cls}>
      {e.label}{estado === "vencido" && dias ? ` · ${dias}d` : ""}
    </Badge>
  );
}

function DueDateCell({ value, onSave, testid }) {
  const [v, setV] = useState(value ? value.slice(0, 10) : "");
  return (
    <input type="date" value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { const nv = v || null; if ((value ? value.slice(0, 10) : null) !== nv) onSave(nv); }}
      className="h-8 rounded-md border border-transparent hover:border-input focus:border-input bg-transparent px-1 text-sm tabular-nums"
      data-testid={testid} />
  );
}

function RowAction({ done, date, onMark, onUndo, verb, testid }) {
  const [d, setD] = useState(todayStr());
  const [busy, setBusy] = useState(false);
  if (done) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{date || "—"}</span>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-muted-foreground hover:text-foreground"
          disabled={busy} onClick={async () => { setBusy(true); await onUndo(); setBusy(false); }} data-testid={`${testid}-undo`}>
          <Undo2 className="h-3.5 w-3.5" /> Deshacer
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-end gap-2">
      <input type="date" value={d} onChange={(e) => setD(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`${testid}-date`} />
      <Button size="sm" className="h-8 gap-1" disabled={busy}
        onClick={async () => { setBusy(true); await onMark(d); setBusy(false); }} data-testid={`${testid}-mark`}>
        <Check className="h-3.5 w-3.5" /> {verb}
      </Button>
    </div>
  );
}

function FinanceTab({ kind }) {
  // kind: "cobros" | "pagos"
  const isCobros = kind === "cobros";
  const endpoint = isCobros ? "/finance/receivables" : "/finance/payables";
  const [estado, setEstado] = useState("pendientes");
  const [q, setQ] = useState("");
  const { data, loading, refetch } = useFetch(endpoint, { estado });
  const rows = data?.rows || [];

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const ql = q.toLowerCase();
    return rows.filter((r) => {
      const partner = isCobros ? "" : (r.supplier_name || "");
      return `${r.project_name} ${r.project_code} ${r.concept} ${r.numero || ""} ${partner}`.toLowerCase().includes(ql);
    });
  }, [rows, q, isCobros]);

  const totalShown = filtered.reduce((a, r) => a + (r.total || 0), 0);

  const mark = async (row, done, date) => {
    try {
      if (isCobros) {
        await api.patch(`/finance/receivables/${row.id}`, done
          ? { status: "collected", collected_date: date }
          : { status: "pending" });
      } else {
        await api.patch(`/finance/payables/${row.id}`, done
          ? { status: "paid", paid_date: date }
          : { status: "pending" });
      }
      toast.success(done ? (isCobros ? "Marcado como cobrado" : "Marcado como pagado") : "Vuelto a pendiente");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const saveDue = async (row, due) => {
    try {
      await api.patch(isCobros ? `/finance/receivables/${row.id}` : `/finance/payables/${row.id}`, { due_date: due });
      toast.success("Fecha prevista actualizada");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar la fecha");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={isCobros ? "Buscar cliente, proyecto, nº factura…" : "Buscar proveedor, proyecto, concepto…"}
            className="pl-9" data-testid={`${kind}-search`} />
        </div>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="w-[210px]" data-testid={`${kind}-estado-filter`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            <SelectItem value={isCobros ? "cobrado" : "pagado"}>{isCobros ? "Cobrados" : "Pagados"}</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-muted-foreground">
          <span className="font-semibold text-foreground tabular-nums">{filtered.length}</span> líneas ·{" "}
          <span className="font-semibold text-foreground tabular-nums">{eur(totalShown)}</span>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[220px]">Proyecto</TableHead>
                <TableHead>{isCobros ? "Nº / Concepto" : "Proveedor / Concepto"}</TableHead>
                <TableHead className="text-right">Importe (IVA incl.)</TableHead>
                <TableHead>{isCobros ? "Cobro previsto" : "Pago previsto"}</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right w-[230px]">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Cargando…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nada por aquí. Todo al día 🎉</TableCell></TableRow>
              )}
              {!loading && filtered.map((r) => {
                const done = isCobros ? r.estado === "cobrado" : r.estado === "pagado";
                return (
                  <TableRow key={r.id} data-testid={`${kind}-row-${r.id}`}>
                    <TableCell>
                      <div className="font-medium truncate max-w-[280px]">{r.project_name || "—"}</div>
                      {r.project_code && <div className="text-xs text-muted-foreground font-mono">{r.project_code}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="truncate max-w-[240px]">{isCobros ? (r.numero || r.concept || "—") : (r.supplier_name || "—")}</div>
                      {!isCobros && r.concept && <div className="text-xs text-muted-foreground truncate max-w-[240px]">{r.concept}</div>}
                      {isCobros && r.numero && r.concept && <div className="text-xs text-muted-foreground truncate max-w-[240px]">{r.concept}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">{eur(r.total)}</TableCell>
                    <TableCell className="text-sm">
                      <DueDateCell value={r.due_date} onSave={(due) => saveDue(r, due)} testid={`${kind}-due-${r.id}`} />
                    </TableCell>
                    <TableCell><EstadoBadge estado={r.estado} dias={r.dias_vencido} /></TableCell>
                    <TableCell className="text-right">
                      <RowAction
                        done={done}
                        date={isCobros ? (r.collected_date && r.collected_date.slice(0, 10)) : (r.paid_date && r.paid_date.slice(0, 10))}
                        verb={isCobros ? "Cobrar" : "Pagar"}
                        onMark={(date) => mark(r, true, date)}
                        onUndo={() => mark(r, false)}
                        testid={`${kind}-${r.id}`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

export default function CobrosPagos() {
  const [tab, setTab] = useState("cobros");
  return (
    <div className="space-y-5" data-testid="cobros-pagos-page">
      <PageHeader
        title="Cobros y pagos"
        subtitle="Marca cobros y pagos de todos los proyectos activos sin entrar a cada ficha. Se refleja en el Cashflow al pulsar «Actualizar desde CRM»."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="cobros" data-testid="tab-cobros" className="gap-2"><ArrowDownCircle className="h-4 w-4" /> Cobros</TabsTrigger>
          <TabsTrigger value="pagos" data-testid="tab-pagos" className="gap-2"><ArrowUpCircle className="h-4 w-4" /> Pagos</TabsTrigger>
        </TabsList>
        <TabsContent value="cobros" className="mt-4"><FinanceTab kind="cobros" /></TabsContent>
        <TabsContent value="pagos" className="mt-4"><FinanceTab kind="pagos" /></TabsContent>
      </Tabs>
    </div>
  );
}
